import { QueryClient, QueryKey } from '@tanstack/react-query';

export type OptimisticListOp = 'add' | 'update' | 'remove';

export interface OptimisticListOptions<T> {
  queryKey: QueryKey;
  op: OptimisticListOp;
  entity: T;
  matchBy: (a: T, b: T) => boolean;
}

export interface OptimisticListContext<T> {
  previous: T[] | undefined;
  queryKey: QueryKey;
}

/**
 * Reusable four-phase optimistic-update helper for list-shaped caches
 * (add/update/remove a row already on screen). Pair with `rollbackList` in
 * `onError` and an `onSettled` invalidation to reconcile against the server.
 */
export async function optimisticList<T>(
  qc: QueryClient,
  { queryKey, op, entity, matchBy }: OptimisticListOptions<T>,
): Promise<OptimisticListContext<T>> {
  await qc.cancelQueries({ queryKey });
  const previous = qc.getQueryData<T[]>(queryKey);
  qc.setQueryData<T[]>(queryKey, (old = []) =>
    op === 'add'
      ? [...old, entity]
      : op === 'update'
      ? old.map((x) => (matchBy(x, entity) ? entity : x))
      : old.filter((x) => !matchBy(x, entity)),
  );
  return { previous, queryKey };
}

export function rollbackList<T>(
  qc: QueryClient,
  ctx: OptimisticListContext<T> | undefined,
): void {
  if (ctx?.previous !== undefined) {
    qc.setQueryData(ctx.queryKey, ctx.previous);
  }
}
